import { Controller } from '@hotwired/stimulus'

// The CCP keypad (ux-design §6.1). Keys build a left-to-right numeric value;
// the within/out-of-limit pill flips live (mirroring
// Compliance::ControlPoint#within_limit?), the Save button morphs to
// "Guardar e investigar" when out of limit, and an out-of-plausibility-band
// value reveals a reason field. The server stays the source of truth — this is
// the fast-feedback sugar that makes the 5-second entry honest.
export default class extends Controller {
  static targets = ['display', 'valueInput', 'pill', 'save', 'reasonField', 'reasonInput']
  static values = {
    comparison: String,
    min: Number,
    max: Number,
    bandLow: Number,
    bandHigh: Number
  }

  connect () {
    this.entry = ''
    this.render()
  }

  press (event) {
    const key = event.currentTarget.dataset.key

    if (key === 'backspace') {
      this.entry = this.entry.slice(0, -1)
    } else if (key === '.') {
      if (!this.entry.includes('.')) this.entry += this.entry === '' ? '0.' : '.'
    } else {
      this.entry += key
    }

    this.render()
  }

  render () {
    this.displayTarget.textContent = this.entry === '' ? '0' : this.entry
    this.valueInputTarget.value = this.entry

    const value = parseFloat(this.entry)
    if (this.entry === '' || isNaN(value)) {
      this.pillTarget.hidden = true
      this.resetSave()
      return
    }

    this.evaluateLimit(value)
    this.evaluateBand(value)
  }

  evaluateLimit (value) {
    const within = this.withinLimit(value)
    const pill = this.pillTarget
    pill.hidden = false
    pill.textContent = within ? pill.dataset.withinText : pill.dataset.outText
    pill.classList.toggle('badge-success', within)
    pill.classList.toggle('badge-error', !within)

    this.saveTarget.textContent = within
      ? this.saveTarget.dataset.withinText
      : this.saveTarget.dataset.outText
    this.saveTarget.classList.toggle('btn-primary', within)
    this.saveTarget.classList.toggle('btn-warning', !within)
  }

  // Mirrors Compliance::ControlPoint#within_limit? — keep the two in sync.
  withinLimit (value) {
    switch (this.comparisonValue) {
      case 'less_than': return value < this.minValue
      case 'less_than_or_equal': return value <= this.minValue
      case 'greater_than': return value > this.minValue
      case 'greater_than_or_equal': return value >= this.minValue
      case 'between': return value >= this.minValue && value <= this.maxValue
      // Tolerance, not strict ===: the server compares with exact BigDecimal,
      // and a typed decimal shouldn't flicker the pill on a float artifact.
      case 'equal': return Math.abs(value - this.minValue) < 1e-9
      default: return true
    }
  }

  // An out-of-band value warns by revealing the reason field; we never block.
  evaluateBand (value) {
    if (!this.hasBandLowValue || !this.hasReasonFieldTarget) return

    const outOfBand = value < this.bandLowValue || value > this.bandHighValue
    this.reasonFieldTarget.hidden = !outOfBand
  }

  resetSave () {
    this.saveTarget.textContent = this.saveTarget.dataset.withinText
    this.saveTarget.classList.add('btn-primary')
    this.saveTarget.classList.remove('btn-warning')
    if (this.hasReasonFieldTarget) this.reasonFieldTarget.hidden = true
  }
}
